/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Observable, EMPTY, Subject } from 'rxjs';
import { IListItemData } from '../interfaces/IListItemData';
import { IListItemEventData } from '../interfaces/IListItemEventData';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { ChangeDetectorRef, ChangeDetectionStrategy, OnInit, Component, OnDestroy } from '@angular/core';
import * as appStrings from '@touchplan/strings';
import { IListItemDef } from '../dialog-reusable.module';
import { MatDialogRef } from '@angular/material/dialog';

export enum ItemType {
	Member = 'Member',
	Role = 'Role',
	Location = 'Location',
	CustomField = 'Custom Field',
}

export enum EditorMode {
	Embed = 'embed',
	Onside = 'onside',
}

@Component({
	selector: 'item-list-editor-base',
	template: '',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export abstract class AbstractItemListEditorComponent<T> implements OnInit, OnDestroy {
	public inAddItem = false;
	public itemInEdit: T; // the item that being edited now
	public editorMode = EditorMode.Embed;
	public isAdmin: boolean;
	public itemFilter: string;
	// These need to be set by subclasses
	public itemType: IListItemDef;
	public self: IItemListEditorDelegate<T>;

	// maintain a single instance of observables
	private itemListObservable$: Observable<T[]>;
	private itemFilterObservable$: Observable<string[]>;
	private itemPaletteColorsObservable$: Observable<string[][]>;
	private itemUsedColors$: Observable<{}>;

	public strings = appStrings;
	protected colorCache = [[]]; // this uses weird Array<Array<string>>
	protected shutdownSubject$ = new Subject<void>();

	protected readonly EMBED_EDIT_DIALOG_WIDTH = '500px';
	protected readonly RIGHT_SIDE_EDIT_DIALOG_WIDTH = '1024px';
	protected readonly DIALOG_HEIGHT = '650px';

	constructor(
		protected changeDetectorRef: ChangeDetectorRef,
		protected dialogRef: MatDialogRef<AbstractItemListEditorComponent<T>>
	) {}

	public ngOnInit(): void {
		this.dialogRef.updateSize(
			this.editorMode === EditorMode.Embed ? this.EMBED_EDIT_DIALOG_WIDTH : this.RIGHT_SIDE_EDIT_DIALOG_WIDTH,
			this.DIALOG_HEIGHT
		);
	}

	public ngOnDestroy(): void {
		// nullify fields to prevent leak
		this.itemListObservable$ = null;
		this.itemFilterObservable$ = null;
		this.itemPaletteColorsObservable$ = null;
		this.itemUsedColors$ = null;
		this.self = null;

		this.shutdownSubject$.next();
		this.shutdownSubject$.complete();
	}

	/** This function is called when user clicks the 'Add Item" button */
	public startAdd(): void {
		this.itemInEdit = null;
		if (this.editorMode === EditorMode.Embed) {
			this.inAddItem = !this.inAddItem;
			//this.virtualScroll.scrollToIndex(0);
		} else {
			this.inAddItem = true;
		}
	}

	/** This function is called when user clicks an existing item to edit it */
	public startEdit(item: T): void {
		if (this.isAdmin) {
			this.inAddItem = false;
			if (this.editorMode === EditorMode.Embed) {
				if (this.itemInEdit === item) {
					this.itemInEdit = null;
				} else {
					this.itemInEdit = item;
				}
			} else {
				if (this.itemInEdit !== item) {
					this.itemInEdit = item;
				}
			}
		}
	}

	/** This function is called when user adding a new item in the dialog and
	 * pressed the Save button. The data in the form is passed
	 *
	 * return boolean value indicating whether the add succeeded or not
	 */
	public abstract completeAdd(emittedData: IListItemEventData<T>): boolean;

	/** This function is called when user editing an existing item in the dialog and
	 *  pressed the Save button. The data in the form is passed in along with the
	 * existing item that is being edited
	 *
	 * return boolean value indicate whether the edit succeeded or not
	 */
	public abstract completeEdit(emittedData: IListItemEventData<T>): boolean;

	/** This function should return that list of items that needs to be displayed
	 * in the dialog. Subclass needs to implement this abstract method to reurn its items
	 */
	protected abstract createItemListObservable(): Observable<T[]>;
	public getItemListObservable(): Observable<T[]> {
		if (!this.itemListObservable$) {
			this.itemListObservable$ = this.createItemListObservable();
		}

		return this.itemListObservable$;
	}

	/** This function should return the data for what to display for a given item
	 */
	public abstract getListItemData(item: T): IListItemData;

	/** This function determines whether the given item matches the search text
	 */
	public abstract doesItemMatchSearch(item: T, searchText: string): boolean;

	/** Drag and Drop support. Called when item dropped to new location
	 */
	public doReorder(event: CdkDragDrop<T[]>, items: T[]) {}

	/** If there is a list of filter values that can be shown ans used to filter the items. Retunr here.
	 * Default is empty
	 */
	protected createItemListFilters(): Observable<string[]> {
		return EMPTY;
	}
	public getItemListFilters(): Observable<string[]> {
		if (!this.itemFilterObservable$) {
			this.itemFilterObservable$ = this.createItemListFilters();
		}

		return this.itemFilterObservable$;
	}

	/** Called when user select a filter if filters are avaialble for this kind of item returned by
	 * getItemListFilters():
	 */
	public applyFilter(filter: string) {
		this.itemFilter = filter;
		this.itemInEdit = null;
	}

	/** This function determines whether the given item matches the filter if any
	 * Default is true
	 */
	public doesItemMatchFilter(item: T): boolean {
		return true;
	}

	/** If a title needs to be displayed for a given item, it can be returned here
	 * Default is not display
	 */
	public getItemDisplayTitle(item: T): string {
		return null;
	}

	//************* We could revisit to decide the best arrangement for color picker support when we have a decision **********/

	/** This function indicate whether the items involved have master color list or not. If yes,
	 * a master color list edit button is show for editing the colors. If null is returned,
	 * it indicate that colors are not involved here and the cmaster color list edit button will not show up
	 */
	protected createItemPaletteColors(): Observable<string[][]> {
		return EMPTY;
	}
	public getItemPaletteColors(): Observable<string[][]> {
		if (!this.itemPaletteColorsObservable$) {
			this.itemPaletteColorsObservable$ = this.createItemPaletteColors();
		}

		return this.itemPaletteColorsObservable$;
	}

	/** This function returns colors among those given by getItemPaletteColors() that are currentlt i nuse
	 * Only apply when  getItemPaletteColors() returns valid value
	 */
	protected createUsedColors(): Observable<{}> {
		return EMPTY;
	}
	public getUsedColors(): Observable<{}> {
		if (!this.itemUsedColors$) {
			this.itemUsedColors$ = this.createUsedColors();
		}

		return this.itemUsedColors$;
	}

	// returns an anonymous function that preserves the scope
	public getAddColorCallback(): (color: string) => void {
		return (color: string) => this.addColor(color);
	}

	protected addColor(color: string): void {
		this.changeDetectorRef.detectChanges();
	}
}

export interface IItemListEditorDelegate<T> extends AbstractItemListEditorComponent<T> {}
